import { useEffect, useId } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import type { FieldErrors } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  DangerButton,
  HStack,
  TertiaryButton,
  Text,
  TextArea,
  VStack,
} from '@enechain/ecloud-designsystem'

import { WarningMessagesAlert } from '~/components/bidding/common/WarningMessagesAlert'
import type { RejectFormValues } from '~/components/bidding/common/rejectSchema'
import { rejectFormSchema } from '~/components/bidding/common/rejectSchema'
import {
  FieldErrorText,
  FieldLabel,
  FieldRoot,
} from '~/components/common/Field'
import { Modal } from '~/components/common/Modal'
import { resolveFormErrorAlertMessage } from '~/libs/formErrors'

type Props = {
  isOpen: boolean
  isLoading: boolean
  onClose: () => void
  onConfirm: (reason: string) => void
  /**
   * 差戻し対象を示す補足文 (= 承認案件一覧のように行を選んで開く導線では対象が
   * 自明なので省略、 スポット入札画面のようにバナーから開く導線では「どの申請
   * セットを差し戻すのか」 を明示する)。
   */
  description?: React.ReactNode
  /**
   * ダイアログの題名。 省略時は共通の「承認申請の差戻し」。 時間前入札は spec が操作ごとの
   * タイトル「入札の差戻し」 を定める (AC-BIN-098) ので、 呼び出し側から渡す。
   */
  title?: string
  /** 確定を止めない注意の文 (例: AC-BSP-098)。 説明文の下に warning Alert で出す。 */
  warningMessages?: readonly string[]
}

/**
 * 差戻し確認ダイアログ (= 承認案件一覧 / スポット入札画面 の共通部品)。
 *
 * 差戻し理由は必須 (approval-tasks.md AC-009 / spot-bidding.md AC-BSP-063)。
 * 未入力は送信ブロック。
 * AC-015: オーバーレイクリックでは閉じない (Modal デフォルト動作)。
 */
export const RejectDialog = ({
  isOpen,
  isLoading,
  onClose,
  onConfirm,
  description,
  title,
  warningMessages,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const reasonId = useId()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    clearErrors,
  } = useForm<RejectFormValues>({
    resolver: zodResolver(rejectFormSchema),
  })

  const handleClose = (): void => {
    reset()
    onClose()
  }

  // 差戻しは fire-and-forget で、 失敗時に parent (index.tsx handleReject) の catch は
  // dialog を閉じない。 onSubmit で reset すると、 RPC 失敗で dialog が開いたまま必須の
  // 差戻し理由だけが消え、 ユーザーが再入力を強いられる。 そのため submit では reset せず、
  // dialog を開くたびに reset して前回入力の持ち越しを防ぐ (成功時の close は handleClose を
  // 通らず setDialogState で閉じるため、 open 契機での reset が必要)。
  const onSubmit = (values: RejectFormValues): void => {
    onConfirm(values.reason)
  }

  // 差戻し理由は単一 field なので通常は深さ 1 = 項目脇の `FieldErrorText` が同じ文言を出しており、
  // alert は重複になる (= helper が undefined を返す)。 message を持たないエラーで
  // 「差戻すボタンが効かない」ように見える事故だけを fallback 文言で拾う。
  const onInvalid = (formErrors: FieldErrors<RejectFormValues>): void => {
    const alertMessage = resolveFormErrorAlertMessage(
      formErrors,
      t('page:biddingCommon.serverError.validation'),
    )
    if (alertMessage !== undefined) {
      setError('root.serverError', { message: alertMessage })
    } else {
      // 直前の失敗で出した alert が残り続けるのを防ぐ。
      clearErrors('root.serverError')
    }
  }

  useEffect(() => {
    if (isOpen) {
      reset()
    }
  }, [isOpen, reset])

  return (
    <Modal
      footer={
        <HStack justifyContent="flex-end" gap={3}>
          <TertiaryButton disabled={isLoading} onClick={handleClose}>
            {t('page:biddingCommon.rejectDialog.cancel')}
          </TertiaryButton>
          <DangerButton
            disabled={isLoading}
            loading={isLoading}
            onClick={() => {
              void handleSubmit(onSubmit, onInvalid)()
            }}
          >
            {t('page:biddingCommon.rejectDialog.confirm')}
          </DangerButton>
        </HStack>
      }
      isOpen={isOpen}
      onClose={handleClose}
      title={title ?? t('page:biddingCommon.rejectDialog.title')}
    >
      <VStack align="stretch" gap={3}>
        {errors.root?.serverError !== undefined && (
          <Alert status="error" title={errors.root.serverError.message} />
        )}
        {description !== undefined && (
          <Box>
            <Text>{description}</Text>
          </Box>
        )}
        <WarningMessagesAlert messages={warningMessages ?? []} />
        {/* label / error は必ず field slot を通す。
            手組みの <label> / <p> だと label recipe の 14px/600 も、 globalCss の
            `[data-scope="field"][data-part="root"][data-invalid]` 起点の赤枠も当たらない。 */}
        <FieldRoot invalid={errors.reason !== undefined}>
          <FieldLabel htmlFor={reasonId}>
            {t('page:biddingCommon.rejectDialog.commentLabel')}
          </FieldLabel>
          <TextArea
            id={reasonId}
            placeholder={t(
              'page:biddingCommon.rejectDialog.commentPlaceholder',
            )}
            rows={3}
            {...register('reason')}
          />
          {errors.reason !== undefined && (
            <FieldErrorText>{errors.reason.message}</FieldErrorText>
          )}
        </FieldRoot>
      </VStack>
    </Modal>
  )
}
